import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, KPICard, Pill, Skeleton } from "../components/Bits";
import { OUTCOME_LABELS, OUTCOME_COLORS, formatDuration, formatRelative } from "../lib/format";
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, FunnelChart, Funnel, LabelList,
} from "recharts";
import { Link } from "react-router-dom";
import { ArrowRightIcon, PhoneCallIcon } from "@phosphor-icons/react";

export default function Dashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        const load = async () => {
            try {
                const { data } = await api.get("/dashboard/overview");
                if (alive) setData(data);
            } finally {
                if (alive) setLoading(false);
            }
        };
        load();
        const id = setInterval(load, 12000);
        return () => { alive = false; clearInterval(id); };
    }, []);

    const kpis = data?.kpis || {};
    const breakdown = data?.outcome_breakdown || {};
    const recent = data?.recent_calls || [];

    const breakdownData = Object.entries(breakdown).map(([k, v]) => ({
        name: OUTCOME_LABELS[k] || k,
        value: v,
        color: OUTCOME_COLORS[k] || "#111827",
    }));

    const funnelData = [
        { name: "Calls", value: kpis.total_calls || 0, fill: "#030712" },
        { name: "Connected", value: Math.max((kpis.total_calls || 0) - (breakdown.no_answer || 0) - (breakdown.voicemail || 0), 0), fill: "#1F2937" },
        { name: "Interested", value: (breakdown.interested || 0) + (breakdown.whitepaper_sent || 0) + (breakdown.appointment_booked || 0), fill: "#0033A0" },
        { name: "Booked", value: kpis.appointments_booked || 0, fill: "#059669" },
    ];

    return (
        <div className="pb-12">
            <PageHeader
                overline="MISSION CONTROL"
                title="Dashboard"
                description="Live operational overview across all campaigns and prospects."
                testid="dashboard-header"
                actions={
                    <Link to="/campaigns" className="btn-primary px-4 py-2.5 text-[13px] flex items-center gap-2" data-testid="dashboard-cta-campaigns">
                        Go to campaigns <ArrowRightIcon size={14} weight="bold" />
                    </Link>
                }
            />

            <div className="px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dashboard-kpis">
                {loading
                    ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-[110px]" />)
                    : (
                        <>
                            <KPICard label="ACTIVE CAMPAIGNS" value={kpis.active_campaigns ?? 0} sub={`${kpis.total_calls ?? 0} calls total`} testid="kpi-campaigns" />
                            <KPICard label="APPOINTMENTS" value={kpis.appointments_booked ?? 0} sub={`${kpis.conversion_rate ?? 0}% conv rate`} accent testid="kpi-appointments" />
                            <KPICard label="WHITEPAPERS SENT" value={kpis.whitepapers_sent ?? 0} sub={`${kpis.interested ?? 0} interested`} testid="kpi-whitepapers" />
                            <KPICard label="AVG SENTIMENT" value={(kpis.avg_sentiment ?? 0).toFixed(2)} sub={`avg call ${formatDuration(kpis.avg_duration_sec || 0)}`} testid="kpi-sentiment" />
                        </>
                    )
                }
            </div>

            <div className="px-8 grid grid-cols-1 lg:grid-cols-3 gap-3">
                <div className="swiss-section p-5 lg:col-span-2" data-testid="dashboard-outcomes-chart">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <div className="overline">CALL OUTCOMES</div>
                            <h3 className="text-xl font-semibold mt-0.5">Distribution</h3>
                        </div>
                        <Link to="/calls" className="text-[12px] underline text-[var(--text-secondary)] hover:text-[var(--text-primary)]" data-testid="link-all-calls">
                            View all →
                        </Link>
                    </div>
                    {breakdownData.length === 0 ? (
                        <div className="h-[260px] flex items-center justify-center text-[var(--text-secondary)] text-[13px]">
                            No calls yet — start a campaign to populate this chart.
                        </div>
                    ) : (
                        <div style={{ height: 260 }}>
                            <ResponsiveContainer>
                                <BarChart data={breakdownData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                                    <CartesianGrid stroke="#E5E7EB" strokeDasharray="0" vertical={false} />
                                    <XAxis dataKey="name" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} stroke="#4B5563" />
                                    <YAxis tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} stroke="#4B5563" allowDecimals={false} />
                                    <Tooltip cursor={{ fill: "#F3F4F6" }} contentStyle={{ borderRadius: 2, border: "1px solid #111827", fontFamily: "IBM Plex Mono", fontSize: 12 }} />
                                    <Bar dataKey="value" fill="#030712" isAnimationActive={false}>
                                        {breakdownData.map((d, i) => (
                                            <Cell key={i} fill={d.color} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>

                <div className="swiss-section p-5" data-testid="dashboard-funnel">
                    <div className="overline">CONVERSION FUNNEL</div>
                    <h3 className="text-xl font-semibold mt-0.5 mb-3">Pipeline shape</h3>
                    <div style={{ height: 240 }}>
                        <ResponsiveContainer>
                            <FunnelChart>
                                <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #111827", fontFamily: "IBM Plex Mono", fontSize: 12 }} />
                                <Funnel dataKey="value" data={funnelData} isAnimationActive={false}>
                                    <LabelList position="right" fill="#030712" stroke="none" dataKey="name" style={{ fontFamily: "IBM Plex Mono", fontSize: 11 }} />
                                </Funnel>
                            </FunnelChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            <div className="px-8 py-6 grid grid-cols-1 lg:grid-cols-3 gap-3">
                <div className="swiss-section lg:col-span-2" data-testid="dashboard-recent-calls">
                    <div className="px-5 py-4 border-b border-[var(--border)] flex items-center justify-between">
                        <div>
                            <div className="overline">LIVE FEED</div>
                            <h3 className="text-xl font-semibold mt-0.5">Recent calls</h3>
                        </div>
                        <span className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-[var(--text-secondary)]">
                            <span className="w-1.5 h-1.5 rounded-full live-dot" /> Auto-refresh 12s
                        </span>
                    </div>
                    {recent.length === 0 ? (
                        <div className="p-12 text-center text-[var(--text-secondary)] text-[13px]">
                            No calls yet. Open a campaign and click <span className="font-mono">Start batch</span>.
                        </div>
                    ) : (
                        <ul className="grid-borders" data-testid="recent-calls-list">
                            {recent.map((c) => (
                                <li key={c.id} className="px-5 py-3 flex items-center gap-4 hover:bg-[var(--surface-active)]">
                                    <PhoneCallIcon size={16} weight="bold" className="shrink-0 text-[var(--text-secondary)]" aria-hidden="true" />
                                    <div className="min-w-0 flex-1">
                                        <Link to={`/calls/${c.id}`} className="font-semibold text-[14px] hover:underline truncate block" data-testid={`recent-call-${c.id}`}>
                                            {c.prospect_name}
                                        </Link>
                                        <div className="text-[12px] text-[var(--text-secondary)] font-mono truncate">
                                            {c.prospect_company || "—"} · {formatDuration(c.duration_sec)} · {formatRelative(c.call_timestamp)}
                                        </div>
                                    </div>
                                    <Pill color={OUTCOME_COLORS[c.outcome] || "#111827"}>{OUTCOME_LABELS[c.outcome] || c.outcome}</Pill>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="swiss-section p-5" data-testid="dashboard-prospects-summary">
                    <div className="overline">DATABASE</div>
                    <h3 className="text-xl font-semibold mt-0.5">Prospect coverage</h3>
                    <div className="mt-4 grid-borders">
                        <Row k="Lists" v={kpis.total_lists ?? 0} />
                        <Row k="Prospects" v={kpis.total_prospects ?? 0} />
                        <Row k="Calls placed" v={kpis.total_calls ?? 0} />
                        <Row k="Avg duration" v={formatDuration(kpis.avg_duration_sec || 0)} />
                    </div>
                    <Link to="/prospects" className="btn-ghost mt-4 inline-flex items-center gap-2 px-3 py-2 text-[12px]" data-testid="link-prospects-page">
                        Manage prospects <ArrowRightIcon size={12} weight="bold" />
                    </Link>
                </div>
            </div>
        </div>
    );
}

const Row = ({ k, v }) => (
    <div className="py-2 flex items-center justify-between">
        <span className="overline">{k}</span>
        <span className="mono font-semibold">{v}</span>
    </div>
);
